import { useState, useCallback, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import { fetchGridItems } from 'api'
import { normalizeEndpointItem } from '@/molecules/Grid/utils'

function parsePageParam(value) {
  const normalizedValue = Array.isArray(value) ? value[0] : value
  const pageNumber = parseInt(normalizedValue, 10)

  return pageNumber > 0 ? pageNumber : 1
}

const usePaginatedGrid = ({
  initialItems = [],
  totalItems = 0,
  postType,
  brandId,
  filter,
  perPage,
  orderBy = 'date',
  recipesDisplayedFilters = [],
  cocktailDisplayedFilters = [],
}) => {
  const router = useRouter()
  const isManualOrder = orderBy === 'manual'
  const effectiveTotalItems = isManualOrder ? initialItems.length : totalItems
  const totalPages = Math.ceil(effectiveTotalItems / perPage)
  const basePath = router.asPath.split('?')[0]
  const initialPage = Math.min(
    parsePageParam(router.query.page),
    Math.max(totalPages, 1)
  )

  const [items, setItems] = useState(initialItems)
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [loadedPage, setLoadedPage] = useState(1)
  const [loading, setLoading] = useState(!isManualOrder && initialPage > 1)
  const isMountFetchDone = useRef(false)
  const loadRequestId = useRef(0)

  useEffect(() => {
    if (!isManualOrder || initialItems.length === totalItems) return

    console.warn(
      `Manual grid item count mismatch: expected ${totalItems}, received ${initialItems.length}. Pagination uses the received item count.`
    )
  }, [initialItems.length, isManualOrder, totalItems])

  const fetchPage = useCallback(async (page) => {
    const displayedFilters = [
      ...(recipesDisplayedFilters.map((term) => term.term_id) || []),
      ...(cocktailDisplayedFilters.map((term) => term.term_id) || []),
    ]

    const data = await fetchGridItems({
      postType,
      brand: brandId,
      filter,
      page,
      perPage,
      order: orderBy,
      displayedFilters,
    })

    return (data?.items || data || []).map(normalizeEndpointItem)
  }, [postType, brandId, filter, perPage, orderBy, recipesDisplayedFilters, cocktailDisplayedFilters])

  const loadUpToPage = useCallback(async (targetPage) => {
    const requestId = loadRequestId.current + 1
    loadRequestId.current = requestId

    if (isManualOrder || targetPage <= loadedPage) {
      setLoading(false)
      setCurrentPage(targetPage)
      return true
    }

    setLoading(true)
    try {
      const pages = Array.from(
        { length: targetPage - loadedPage },
        (_, index) => loadedPage + index + 1
      )
      const pageItems = await Promise.all(pages.map(fetchPage))

      if (requestId !== loadRequestId.current) return false

      setItems((previousItems) => [
        ...previousItems,
        ...pageItems.flat(),
      ])
      setLoadedPage(targetPage)
      setCurrentPage(targetPage)
      return true
    } catch (error) {
      console.error('Error loading paginated grid items:', error)
      return false
    } finally {
      if (requestId === loadRequestId.current) {
        setLoading(false)
      }
    }
  }, [fetchPage, isManualOrder, loadedPage])

  useEffect(() => {
    if (isMountFetchDone.current) return
    const pageParam = parsePageParam(router.query.page)
    if (pageParam > 1) {
      isMountFetchDone.current = true
      loadUpToPage(Math.min(pageParam, totalPages))
    }
  }, [router.query.page, loadUpToPage, totalPages])

  useEffect(() => {
    const handlePopState = async () => {
      const searchParams = new URLSearchParams(window.location.search)
      const pageParam = parseInt(searchParams.get('page'), 10) || 1
      const targetPage = Math.min(Math.max(pageParam, 1), totalPages)

      await loadUpToPage(targetPage)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [loadUpToPage, totalPages])

  const handleLoadMore = useCallback(async () => {
    if (loading || currentPage >= totalPages) return

    const nextPage = currentPage + 1
    const pageLoaded = await loadUpToPage(nextPage)
    if (!pageLoaded) return

    const nextPageUrl = `${basePath}?page=${nextPage}`
    window.history.pushState(
      { paginatedGridPage: nextPage },
      '',
      nextPageUrl
    )
  }, [loading, currentPage, totalPages, loadUpToPage, basePath])

  const visibleItems = items.slice(0, currentPage * perPage)
  const hasMore = currentPage < totalPages

  return {
    items: visibleItems,
    loading,
    hasMore,
    currentPage,
    totalPages,
    basePath,
    handleLoadMore,
  }
}

export default usePaginatedGrid
